import React, { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAllProfiles } from "@/v2/hooks/profiles/useAllProfiles";
import { usePortfolioFeed } from "@/v2/hooks/profiles/usePortfolioFeed";
import { DISCIPLINES } from "@/data/constants";
import { ALL_COUNTRIES } from "@/data/countries";
import SearchSection from "./filters/SearchSection";
import { Profile } from "@/hooks/designer-grid/types";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import PortfolioFeedCard from "./portfolio/PortfolioFeedCard";
import { Skeleton } from "./ui/skeleton";
import PaginationControls from "./designer-grid/PaginationControls";
import ServicesSection from "@/components/dashboard/content/ServicesSection";
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from "@tanstack/react-query";
import MobilePortfolioModal from './profile/portfolio/modal/MobilePortfolioModal';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";

interface PortfolioGridProps {
  selectedDisciplines?: string[];
  selectedLocations?: string[];
  onDisciplineToggle?: (discipline: string) => void;
  onLocationToggle?: (location: string) => void;
  onResetFilters?: () => void;
  showFilters?: boolean;
  onPageChange?: (page: number) => void;
}

const PortfolioGrid = ({
  selectedDisciplines = [],
  selectedLocations = [],
  onDisciplineToggle,
  onLocationToggle,
  onResetFilters,
  showFilters = true,
  onPageChange,
}: PortfolioGridProps) => {
  const { toast } = useToast();

  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState<any>(null);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  const [previousPage, setPreviousPage] = useState(1);

  const isMobile = useIsMobile();

  const handleOpenPortfolioModal = (profile: any, item: any) => {
    setSelectedProfile(profile);
    setSelectedPortfolioItem(item);
  };
  const handleClosePortfolioModal = () => {
    setSelectedProfile(null);
    setSelectedPortfolioItem(null);
  };

  const {
    data,
    isLoading,
    isError,
    handlePageChange: allProfilesPageChange,
    refetch,
    filters: {
      selectedLocations: currentSelectedLocations,
      selectedDisciplines: currentSelectedDisciplines,
      handleLocationToggle: currentHandleLocationToggle,
      handleDisciplineToggle: currentHandleDisciplineToggle,
      handleFilterReset: currentHandleFilterReset,
    },
  } = useAllProfiles();

  // Use provided filters or fall back to current ones
  const effectiveSelectedLocations = selectedLocations.length > 0 ? selectedLocations : currentSelectedLocations;
  const effectiveSelectedDisciplines = selectedDisciplines.length > 0 ? selectedDisciplines : currentSelectedDisciplines;
  const effectiveHandleLocationToggle = onLocationToggle || currentHandleLocationToggle;
  const effectiveHandleDisciplineToggle = onDisciplineToggle || currentHandleDisciplineToggle;
  const effectiveHandleFilterReset = onResetFilters || currentHandleFilterReset;

  // Use the real portfolio feed data instead of mock data
  const { 
    data: portfolioFeedData, 
    isLoading: portfolioLoading, 
    error: portfolioError,
    handlePageChange: portfolioPageChange
  } = usePortfolioFeed();

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      portfolioPageChange(1);
      refetch();
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener('refresh', handleRefresh);
    return () => {
      window.removeEventListener('refresh', handleRefresh);
    };
  }, [refetch, portfolioPageChange]);

  const handlePaginationChange = (page: number) => {
    portfolioPageChange(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleAllReset = () => {
    effectiveHandleFilterReset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Use portfolio feed pagination data
  const pagination: ContentPagination = {
    currentPage: portfolioFeedData?.pagination?.currentPage || 1,
    totalPages: portfolioFeedData?.pagination?.totalPages || 1,
    hasNextPage: !!portfolioFeedData?.pagination?.hasNextPage,
    hasPrevPage: !!portfolioFeedData?.pagination?.hasPrevPage,
    totalCount: portfolioFeedData?.pagination?.totalCount || 0,
  };

  // Check if we're on the first page
  const isFirstPage = pagination.currentPage === 1;

  // Watch for page changes and scroll to top
  useEffect(() => {
    if (pagination.currentPage !== previousPage) {
      setPreviousPage(pagination.currentPage);
      // Scroll to top after the component has updated
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    }
  }, [pagination.currentPage, previousPage]);

  // Debug pagination data
  console.log('PortfolioGrid: Pagination data', {
    portfolioFeedData: portfolioFeedData?.pagination,
    pagination,
    profilesCount: portfolioFeedData?.profiles?.length
  });

  // Log all profiles for debugging
  console.log('All profiles:', portfolioFeedData?.profiles);

  // Remove useEffect and local state for services/servicesLoading
  const fetchServices = async () => {
    const { data, error } = await supabase
      .from('services')
      .select('id, slug, label, description, image_url, service_roles(role), order')
      .order('order', { ascending: true, nullsLast: true });
    if (error || !data) return [];
    return data.map((service: any) => ({
      ...service,
      professions: service.service_roles.map((r: any) => r.role),
      image: service.image_url,
    }));
  };

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["services"],
    queryFn: fetchServices,
  });

  const verifiedProfiles = portfolioFeedData?.profiles?.filter(p => p.is_featured) || [];
  const allProfiles = portfolioFeedData?.profiles || [];

  const filteredProfiles = useMemo(() => {
    if (!selectedService) return allProfiles;
    return allProfiles.filter(profile =>
      selectedService.professions.some(
        p => p.toLowerCase().trim() === (profile.profession || '').toLowerCase().trim()
      )
    );
  }, [selectedService, allProfiles]);

  if (isLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        {showFilters && (
          <SearchSection
            selectedDisciplines={effectiveSelectedDisciplines}
            selectedLocations={effectiveSelectedLocations}
            onDisciplineToggle={effectiveHandleDisciplineToggle}
            onLocationToggle={effectiveHandleLocationToggle}
            onResetFilters={handleAllReset}
            disciplines={DISCIPLINES}
            locations={ALL_COUNTRIES}
          />
        )}
        <ServicesSection />
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4 md:space-y-6">
        {showFilters && (
          <SearchSection
            selectedDisciplines={effectiveSelectedDisciplines}
            selectedLocations={effectiveSelectedLocations}
            onDisciplineToggle={effectiveHandleDisciplineToggle}
            onLocationToggle={effectiveHandleLocationToggle}
            onResetFilters={handleAllReset}
            disciplines={DISCIPLINES}
            locations={ALL_COUNTRIES}
          />
        )}
        <ServicesSection />
        <div className="text-center py-12">
          <p className="text-gray-500">Something went wrong loading portfolios.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {showFilters && (
        <SearchSection
          selectedDisciplines={effectiveSelectedDisciplines}
          selectedLocations={effectiveSelectedLocations}
          onDisciplineToggle={effectiveHandleDisciplineToggle}
          onLocationToggle={effectiveHandleLocationToggle}
          onResetFilters={handleAllReset}
          disciplines={DISCIPLINES}
          locations={ALL_COUNTRIES}
        />
      )}
      {isFirstPage && (
        <ServicesSection
          verifiedProfiles={verifiedProfiles}
          selectedService={selectedService}
          onServiceClick={setSelectedService}
          services={services}
          servicesLoading={servicesLoading}
        />
      )}
      {selectedService && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-gray-500">Selected filters:</span>
          <span className="bg-orange-500 text-white px-4 py-1 rounded-full flex items-center gap-2 font-semibold">
            {selectedService.label}
            <button
              className="ml-1 text-white hover:text-gray-200 focus:outline-none"
              onClick={() => setSelectedService(null)}
              aria-label="Remove filter"
              type="button"
            >
              ×
            </button>
          </span>
          <button
            className="ml-2 underline text-gray-500 hover:text-orange-500 focus:outline-none"
            onClick={() => setSelectedService(null)}
            type="button"
          >
            Clear all
          </button>
        </div>
      )}
      {/* Updated Portfolios section title and description */}
      <div className="flex md:items-baseline justify-between mb-8">
        <div>
          <h2 className="text-base md:text-xl font-bold opacity-7 text-[#09090B]">Explore</h2>
          <p className="text-[#6B7280] text-xs md:text-sm hidden md:block">See what our designers are working on</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (!onlyVerified) {
                setShowVerifiedModal(true);
              } else {
                setOnlyVerified(false);
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              onlyVerified ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                onlyVerified ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>

          <span className="text-xs md:text-sm text-gray-500">Only verified</span>
        </div>
      </div>
      {/* Portfolio Feed - Responsive Grid Layout (matches designers tab) */}
      {portfolioLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ))}
        </div>
      ) : portfolioError ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Something went wrong loading portfolios.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      ) : !portfolioFeedData?.profiles || portfolioFeedData.profiles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No featured portfolios found.</p>
          {effectiveSelectedDisciplines.length > 0 || effectiveSelectedLocations.length > 0 ? (
            <button 
              onClick={handleAllReset}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Clear Filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {filteredProfiles.map(profile => (
              <PortfolioFeedCard 
                key={profile.id} 
                profile={profile} 
                onOpenPortfolioModal={handleOpenPortfolioModal}
                isMobile={isMobile}
              />
            ))}
          </div>
          <PaginationControls
            pagination={pagination}
            onPageChange={handlePaginationChange}
          />
        </>
      )}
      {isMobile && selectedProfile && selectedPortfolioItem && (
        <MobilePortfolioModal
          item={selectedPortfolioItem}
          isOpen={!!selectedPortfolioItem}
          onClose={handleClosePortfolioModal}
          username={selectedProfile.username || selectedProfile.username_slug || ''}
          username_slug={selectedProfile.username_slug || selectedProfile.username || ''}
          profile={selectedProfile}
        />
      )}

      {/* Partner-Only Feature Modal */}
      <Dialog open={showVerifiedModal} onOpenChange={setShowVerifiedModal}>
        <DialogContent className="max-w-2xl  mx-auto p-0 overflow-hidden rounded-xl">
          <div 
            className="relative w-11/12 mx-auto md:w-full md:h-96 h-[420px] bg-cover bg-center bg-no-repeat rounded-xl"
            style={{ backgroundImage: 'url("/only_verified_modal.png")' }}
          >
            {/* Content */}
            <div className="relative z-10 p-6 h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-center relative">
                <h2 className="text-lg md:text-xl font-medium text-black mb-4">Partner-Only Feature</h2>
                <button
                  onClick={() => setShowVerifiedModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors absolute -top-1 md:right-4 right-1"
                >
                  <X size={20} className="text-black" />
                </button>
              </div>
              
              {/* Content */}
              <div className="text-center">
                <p className="text-[#475467] text-sm md:text-base">
                  Verified designers are available to select partners.
                </p>
                <p className="text-[#475467] text-sm md:text-base">
                  If your team needs early access, reach out.
                </p>
                <a
                  href="mailto:get@world-class.design"
                  className="text-xs md:text-base text-blue-600 hover:text-blue-800"
                >
                  get@world-class.design
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioGrid; 